import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/auth';
import { useActiveLocationId } from '@/lib/locationContext';
import { randomUUID } from '@/lib/uuid';
import { isCell } from '@/lib/cell/config';
import { cellGet } from '@/lib/cell/api';
import { cellSign, cellUpload } from '@/lib/cell/files';
import { saveProProfile, crmMirrorProblem } from '@/lib/proProfile';
import { MAX_COI_FILES, coiFormProblem, isCertificateFile, localToday } from '@/lib/coiForm';

/**
 * COI Upload — Wave 4 of n8n elimination.
 *
 * Pro Hub stays simple per ADR-022: details + document → thank you → done.
 * License path lives on My Info as an inline edit (separate UX).
 *
 * THE PRO TYPES THE NEW POLICY (decision b0954f6f, Peter 2026-10-05: "make it that she enters the
 * new policy info, and just attach the doc. the process check failure should not stop the
 * process"). The upload used to send ONLY the file, and the door filled every other fact from the
 * policy it replaced — so a renewal showed last year's expiry. Now the form asks for carrier, policy
 * number, effective and expiration dates, and files them WITH the document in one filing.
 *
 * Backend (per ADR-021):
 *   1. Files → Supabase Storage compliance-docs/{location_id}/{tenant_id}/
 *   2. pro-profile-update → compliance_file(): the one door (COMPLIANCE-0), as the pro, the same
 *      path My Info's Insurance card uses — lands pending, supersedes the last filing, and mirrors
 *      the details to Zoho. Every attached file reaches the row (file_urls), the first is the document.
 *   3. Database webhook fires coi-extract, which CHECKS the document against what was typed and
 *      leaves the owner a note where they differ — it never overwrites the pro's details or blocks.
 *   4. If issues, owner reaches out via existing relationship — platform
 *      doesn't insert itself between owner and Pro for problem resolution.
 */

/**
 * HUB-056: take back the files a failed press uploaded. Best-effort — a removal that fails leaves the
 * file where it was (the old behaviour) and never changes what the pro is told about the filing.
 */
export async function removeUnfiled(paths: string[]): Promise<void> {
  // CELL (M28): no remove door on the cell. Supabase allowed it only through the rule "the uploader, within a day,
  // while no record references the file" (compliance_docs_uploader_removes_unfiled), and that rule needs an object
  // index (who uploaded what, when) the cell does not have (M14 "gap: object index", files.ts). Skipped, so an unfiled
  // upload stays in place on the cell build — the behaviour before HUB-056. Reported to the M28 session.
  if (isCell()) return;
  try {
    const { error } = await supabase.storage.from('compliance-docs').remove(paths);
    if (error) console.error('[coi] could not remove unfiled uploads', error.message);
  } catch (e) {
    console.error('[coi] could not remove unfiled uploads', e);
  }
}

export default function CoiUpload() {
  const navigate = useNavigate();
  const { session } = useSession();
  const userId = session?.user?.id;
  const { activeLocationId } = useActiveLocationId();

  const [proCtx, setProCtx] = useState<{
    proId: string; tenantId: string; locationId: string;
    firstName: string; lastName: string; email: string; businessName: string;
  } | null>(null);

  const [files, setFiles] = useState<File[]>([]);

  const [crmNote, setCrmNote] = useState<string | null>(null);
  const [carrier, setCarrier] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    if (!userId) return;
    (async () => {
      type Pro = { id: string; first_name: string | null; last_name: string | null; email: string | null };
      type Tenant = { id: string; location_id: string; first_name: string | null; last_name: string | null; email: string | null; salon_name: string | null };
      let pro: Pro | null; let tenant: Tenant | null;
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/users/{authUserId}/coi-context — both reads below (pros, then tenants) in one.
        const { data } = await cellGet<{ pro: Pro | null; tenant: Tenant | null }>(
          `/v1/pro-hub/users/${userId}/coi-context`, { location: activeLocationId });
        pro = data?.pro ?? null; tenant = data?.tenant ?? null;
        if (!pro) return;
      } else {
      ({ data: pro } = await supabase
        .from('pros').select('id, first_name, last_name, email')
        .eq('auth_user_id', userId).maybeSingle());
      if (!pro) return;
      // Phase 4e (2026-04-26): legacy_fields dropped; salon_name reads
      // direct from canonical top-level column.
      // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
      ({ data: tenant } = await supabase
        .from('tenants').select('id, location_id, first_name, last_name, email, salon_name')
        .eq('pro_id', pro.id)
        .eq(
          // Scope to active location for multi-location Pros; if no
          // active location yet (rare — operator-only contexts),
          // PostgREST eq with a falsy value would short-circuit, so
          // we fall through with a no-op match condition.
          activeLocationId ? 'location_id' : 'pro_id',
          activeLocationId ?? pro.id,
        )
        .order('updated_at', { ascending: false }).limit(1).maybeSingle());
      }
      if (!tenant) return;
      const businessName = tenant.salon_name ?? '';
      setProCtx({
        proId: pro.id,
        tenantId: tenant.id,
        locationId: tenant.location_id,
        firstName: tenant.first_name ?? pro.first_name ?? '',
        lastName:  tenant.last_name  ?? pro.last_name  ?? '',
        email:     tenant.email      ?? pro.email      ?? '',
        businessName,
      });
    })();
  }, [userId, activeLocationId]);

  function addFiles(list: FileList | File[] | null) {
    if (!list) return;
    const filtered = Array.from(list).filter(isCertificateFile);
    // HUB-056: never hold more than the filing door takes — the extra pages are refused here, said
    // plainly, before a single upload, instead of after every upload when the filing is rejected.
    const room = Math.max(0, MAX_COI_FILES - files.length);
    if (filtered.length > room) {
      setStatus('error');
      setErrMsg(`A filing takes at most ${MAX_COI_FILES} files — ${filtered.length - room} of those were not added.`);
    }
    setFiles(prev => [...prev, ...filtered.slice(0, Math.max(0, MAX_COI_FILES - prev.length))]);
  }
  function removeFile(i: number) {
    setFiles(prev => prev.filter((_, idx) => idx !== i));
  }

  async function handleSubmit() {
    if (status === 'submitting') return;
    if (!proCtx) {
      setStatus('error'); setErrMsg("Couldn't determine your studio — try refreshing.");
      return;
    }
    const problem = coiFormProblem({
      carrier, policyNumber, effectiveDate, expirationDate, fileCount: files.length,
    });
    if (problem) {
      setStatus('error'); setErrMsg(problem);
      return;
    }
    setStatus('submitting'); setErrMsg('');

    // HUB-056 (fix-p2): every object this press uploaded, so a failure before the filing lands can
    // take them back out (compliance_docs_uploader_removes_unfiled: the uploader, within a day, only
    // while no record references the file). Once the filing has a row, nothing is removed.
    const uploaded: string[] = [];
    let filed = false;
    try {
      // Generate a UUID upfront so files can live under it before the row exists
      const draftId = randomUUID();
      const fileUrls: string[] = [];
      const stamp = Date.now();

      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const safeName = f.name.replace(/[^A-Za-z0-9.\-_]/g, '_');
        const path = `${proCtx.locationId}/${proCtx.tenantId}/${draftId}/${stamp}-${i + 1}-${safeName}`;
        // CELL (M28): POST /v1/pro-hub/files/upload, then a PUT straight to S3 (the compliance-docs tenant-folder rule).
        const { error: upErr } = isCell()
          ? await cellUpload('compliance-docs', path, f, f.type)
          : await supabase.storage
            .from('compliance-docs').upload(path, f, {
              contentType: f.type, upsert: false,
            });
        if (upErr) throw upErr;
        uploaded.push(path);
        // 1-year signed URL — owners can view, Edge Function can extract
        // CELL (M28): POST /v1/pro-hub/files/sign — the API caps a link at 7 days (an M14 finding, files.ts).
        const { data: signed } = isCell()
          ? await cellSign('compliance-docs', path, 60 * 60 * 24 * 365)
          : await supabase.storage
            .from('compliance-docs').createSignedUrl(path, 60 * 60 * 24 * 365);
        if (signed?.signedUrl) fileUrls.push(signed.signedUrl);
      }

      if (fileUrls.length !== files.length) {
        throw new Error("Couldn't attach your certificate — please try again.");
      }

      // COMPLIANCE-0: the ONE door, compliance_file — reached through pro-profile-update, the same
      // single write path My Info's Insurance card uses, so the new policy reaches Zoho too. The
      // door's fences are inside it (a pro files only for their own current tenancy), the filing
      // lands pending, and it SUPERSEDES the current insurance row. Because this filing attaches a
      // NEW document, it carries only the facts typed here — never the replaced policy's expiry.
      const saved = await saveProProfile({
        record_type: 'insurance',
        carrier: carrier.trim() || null,
        document_number: policyNumber.trim(),
        issue_date: effectiveDate || null,
        expiry_date: expirationDate,
        document_url: fileUrls[0],
        file_urls: fileUrls,
      }, proCtx.tenantId);
      filed = true;
      // A server that predates this form would file the details and silently drop the document.
      const row = saved?.compliance as { document_url?: string | null } | null | undefined;
      if (row && row.document_url !== fileUrls[0]) {
        throw new Error("Your policy details were saved, but the certificate didn't attach. Please try again.");
      }

      setCrmNote(crmMirrorProblem(saved)); // HUB-068: filed here, but did the studio's CRM take it?
      setStatus('success');
      setFiles([]);
      setCarrier(''); setPolicyNumber(''); setEffectiveDate(''); setExpirationDate('');
    } catch (err) {
      if (!filed && uploaded.length) await removeUnfiled(uploaded);
      setStatus('error');
      setErrMsg((err as Error).message);
    }
  }

  if (status === 'success') {
    return (
      <div className="card text-center py-12">
        <div className="size-14 rounded-full bg-image-pink-gradient text-white flex items-center justify-center mx-auto mb-4 text-2xl">✓</div>
        <h2 className="font-display text-2xl font-bold text-image-black mb-2">COI Received</h2>
        <p className="text-sm text-image-charcoal max-w-sm mx-auto leading-relaxed">
          Thank you. We received your Certificate of Insurance. We'll review it and follow up if anything is missing.
        </p>
        {crmNote && <p role="status" className="text-xs text-amber-700 max-w-sm mx-auto mt-3" data-testid="crm-mirror-problem">{crmNote}</p>}
        <div className="flex gap-2 justify-center mt-6">
          <button onClick={() => { setStatus('idle'); setFiles([]); }} className="btn-ghost">
            Upload Another
          </button>
          <button onClick={() => navigate('/my-info')} className="btn-primary !bg-image-pink hover:!bg-image-plum">
            Back to My Info
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-image-black">
          Upload Your <span className="text-image-pink">COI</span>
        </h1>
        <p className="text-sm text-image-charcoal mt-1">
          Enter your policy details, attach your Certificate of Insurance, and tap Submit.
        </p>
      </div>

      <div className="card space-y-5">
        {proCtx && (
          <div className="text-xs text-image-charcoal bg-image-fog rounded-lg p-3">
            Submitting as <strong className="text-image-black">{proCtx.firstName} {proCtx.lastName}</strong> ({proCtx.email})
            {proCtx.businessName && <> — {proCtx.businessName}</>}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="block text-xs font-semibold text-image-black mb-1">Insurance carrier</span>
            <input className="input" value={carrier} onChange={e => setCarrier(e.target.value)}
              placeholder="e.g. The Hartford" autoComplete="off" />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-image-black mb-1">Policy number</span>
            <input className="input" value={policyNumber} onChange={e => setPolicyNumber(e.target.value)}
              placeholder="Policy number" autoComplete="off" />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-image-black mb-1">Effective date</span>
            <input type="date" className="input" value={effectiveDate}
              onChange={e => setEffectiveDate(e.target.value)} aria-label="Effective date" />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-image-black mb-1">Expiration date</span>
            <input type="date" className="input" value={expirationDate} min={localToday()}
              onChange={e => setExpirationDate(e.target.value)} aria-label="Expiration date" required />
          </label>
        </div>

        <div>
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
              dragOver ? 'border-image-pink bg-image-pink-soft/20'
              : files.length > 0 ? 'border-emerald-500/50 bg-emerald-50'
              : 'border-image-border hover:border-image-pink/50 bg-image-fog/40'
            }`}
          >
            <input type="file" accept=".pdf,image/*" multiple
              onChange={e => addFiles(e.target.files)}
              className="absolute inset-0 opacity-0 cursor-pointer" />
            {files.length > 0 ? (
              <div className="space-y-2">
                <div className="text-2xl">📄</div>
                <div className="text-sm font-semibold text-image-black">
                  {files.length} file{files.length > 1 ? 's' : ''} ready
                </div>
                <div className="space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs bg-white rounded-lg px-3 py-1.5 border border-image-border">
                      <span className="truncate text-image-black">{f.name}</span>
                      <span className="text-image-charcoal shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                      <button onClick={e => { e.stopPropagation(); removeFile(i); }} className="text-image-charcoal hover:text-red-500 shrink-0">×</button>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-image-charcoal">Tap to add more</div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="text-2xl">⬆️</div>
                <div className="text-sm text-image-charcoal">
                  Drop your COI here or tap to browse
                </div>
                <div className="text-[11px] text-image-charcoal">PDF or photo</div>
              </div>
            )}
          </div>
        </div>

        {status === 'error' && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            {errMsg}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={!files.length || !policyNumber.trim() || !expirationDate || !proCtx || status === 'submitting'}
          className="btn-primary w-full !bg-image-pink hover:!bg-image-plum disabled:!bg-image-light-gray disabled:!text-image-charcoal"
        >
          {status === 'submitting' ? 'Uploading…' : 'Submit COI'}
        </button>

        <p className="text-[11px] text-image-charcoal text-center leading-relaxed">
          We'll review it and reach out if anything is missing.
        </p>
      </div>
    </div>
  );
}
